import { useAddressForm } from "./use-address-form";

import { css } from "@emotion/react";

import React, { FormEvent } from "react";
import { SwarovskiOptikFormInputElement } from "../../../shared/form/SwarovskiOptikFormInputElement";
import { jsHooks } from "@swo/shared";
import { fontSize } from "../../../../../../../styles/fonts";
import { useTrackedEffect } from "../../../../../../../hooks/use-tracked-effect";
import { ClientCartAddress_Second, SwarovskiOptikShopLocaleFrontend, type CheckoutTranslation, type TranslatedValue } from "@swo/storefront";
import { InternalAddress } from "./address";
import { Translated } from "../../../../../../../domain/translations/translations";
import { CSS_MQ } from "../../../../../../../styles/breakpoints";

import { ShowAddressHeadline } from "./ShowAddressHeadline";
import { AddressFormMode } from "./address-form-mode";
import { ListOfAddressedErrors } from "../../../state/get-validation-errors";
import { SwarovskiOptikStyledDropdown } from "./SwarovskiOptikStyledDropdown";
import { SwarovskiOptikPhoneField } from "./SwarovskiOptikPhoneField";
import { AddressActionButtons } from "./AddressActionButtons";
import { InternationalPhoneNumber } from "./international-phone-number/international-phone-number";
import { T } from "../../../../../../../components/T";

type CheckoutAddressFormEditorProps<Address extends ClientCartAddress_Second> = {
  className: string;
  primaryAction: {
    callback: (checkoutFormValidData: Address) => Promise<void>;
    label: TranslatedValue;
    className?: string;
  };
  secondaryAction?: {
    callback: () => void;
    label: TranslatedValue;
  };
  onChangeCountryRequested: (countryIsoCode: string) => void;
  countries: Array<SwarovskiOptikShopLocaleFrontend> | null;

  locale: SwarovskiOptikShopLocaleFrontend;
  address: Address | undefined;
  mode: AddressFormMode;
  headline: TranslatedValue;
  translations: Translated<CheckoutTranslation>;
  children?: React.ReactNode;

  validationErrors: ListOfAddressedErrors;
  isSubmitDisabled: boolean;
};

export function CheckoutAddressFormEditor<Address extends ClientCartAddress_Second>(
  props: CheckoutAddressFormEditorProps<Address>,
) {
  const [isPending, trackedAction] = useTrackedEffect(trySubmittingForm, { onlyOnePending: true });

  const [formFields, formStateValue] = useAddressForm<Address>(
    props.address,
    props.translations,
    props.locale,
    props.mode,
    props.onChangeCountryRequested,
    props.countries,
  );

  function trySubmittingForm(state: InternalAddress): Promise<void> {
    const canEditUserEmail = props.mode === "shipping";
    const countryIsoCode = props.locale.country.isoCode;

    const _: ClientCartAddress_Second & { email?: string; firstName?: string; lastName?: string } = {
      email: canEditUserEmail ? state.email : undefined,
      firstName: state.firstName,
      lastName: state.lastName,

      phone: InternationalPhoneNumber.toString(state.phoneNumber, countryIsoCode),

      streetName: state.addressLineOne,
      additionalAddressInfo: state.addressLineTwo,

      city: state.city,
      postalCode: state.postalCode,
      state: state.state,
      country: countryIsoCode,
    };

    //@ts-expect-error
    return props.primaryAction.callback(_);
  }

  function onFormSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();

    void trackedAction(formStateValue);
  }

  return (
    <div className={props.className}>
      <ShowAddressHeadline
        css={css`
          display: inline-block;
          margin-bottom: 15px;
        `}
      >
        <T>{props.headline}</T>
      </ShowAddressHeadline>

      <form onSubmit={onFormSubmit} className={jsHooks.checkout.addressForm.form}>
        <div css={formGridStyles}>
          {formFields.map(def => {
            const { field, ...shared } = def;
            const maybeFieldValidation = props.validationErrors.find(_ => _.address === def.fieldName)?.label;

            switch (field.tag) {
              case "input": {
                return (
                  <SwarovskiOptikFormInputElement
                    key={def.key}
                    element={def}
                    value={field.value(formStateValue) || ""}
                    onChange={field.onChange}
                    className={jsHooks.checkout.addressForm.field(def.key)}
                    errorMessage={maybeFieldValidation}
                  />
                );
              }
              case "dropdown": {
                return (
                  <SwarovskiOptikStyledDropdown
                    key={def.key}
                    element={def}
                    def={{ field, ...shared }}
                    translations={props.translations}
                    formStateValue={formStateValue}
                    errorMessage={maybeFieldValidation}
                  />
                );
              }
              case "phone": {
                return (
                  <SwarovskiOptikPhoneField
                    key={def.key}
                    def={{ field, ...shared }}
                    translations={props.translations}
                    formStateValue={formStateValue}
                    errorMessage={maybeFieldValidation}
                  />
                );
              }

              default: {
                const _: never = field;
                return _;
              }
            }
          })}
        </div>

        <div
          css={css`
            margin-top: 30px;
          `}
        >
          <AddressActionButtons
            primaryAction={props.primaryAction}
            secondaryAction={props.secondaryAction}
            primaryActionStatus={isPending ? "pending" : props.isSubmitDisabled ? "disabled" : "normal"}
          />

          <div>{props.children}</div>
        </div>
      </form>
    </div>
  );
}

export const swarovskiDropdownStyles = [
  fontSize.x2,
  css`
    width: 100%;
    height: 40px;

    outline: none;

    letter-spacing: inherit;
    background: transparent;

    border-bottom: none;
  `,
];

const formGridStyles = css`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));

  ${CSS_MQ.atLeastSm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  gap: 16px;
`;
